
import React from 'react';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileTextIcon, CheckCircleIcon, DownloadIcon } from "lucide-react";

interface BenposPayoutsProps {
  data: any[];
  tdsWaivers: Record<string, boolean>;
}

export const BenposPayouts: React.FC<BenposPayoutsProps> = ({ data, tdsWaivers }) => {
  // Calculate total payout amounts
  const totalInterest = data.reduce((sum, item) => {
    return sum + ((8.75 / 100) * item.faceValue);
  }, 0);
  
  const totalTds = data.reduce((sum, item) => {
    const interestAmount = (8.75 / 100) * item.faceValue;
    const hasTdsWaiver = tdsWaivers[item.folio];
    return sum + (hasTdsWaiver ? 0 : (10 / 100) * interestAmount);
  }, 0);
  
  const totalNetPayout = totalInterest - totalTds;
  
  // Generate payment date
  const paymentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  // Generate UTR number
  const utrNumber = 'UTR' + Math.floor(Math.random() * 10000000000).toString().padStart(10, '0');
  
  return (
    <div className="space-y-6">
      {/* Payment Summary Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle>Payment Receipt</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">Reference: {utrNumber}</p>
            </div>
            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
              <CheckCircleIcon className="h-4 w-4 mr-1" />
              Payment Completed
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Payment Date</p>
              <p className="font-medium">{paymentDate}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Beneficiaries</p>
              <p className="font-medium">{data.length}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Amount</p>
              <p className="font-medium">₹{totalNetPayout.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
            </div>
          </div>
          
          <div className="mt-6 pt-6 border-t">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Interest Calculated</p>
                <p className="font-medium">₹{totalInterest.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">TDS Deducted</p>
                <p className="font-medium">₹{totalTds.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">TDS Waivers Applied</p>
                <p className="font-medium">{Object.values(tdsWaivers).filter(Boolean).length}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Source Account</p>
                <p className="font-medium">Primary Treasury Account</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Detailed Payment Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Folio Number</TableHead>
              <TableHead>Beneficiary Name</TableHead>
              <TableHead className="text-right">Interest Amount</TableHead>
              <TableHead>TDS</TableHead>
              <TableHead className="text-right">Net Amount Paid</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item, index) => {
              // Calculate payment details
              const interestAmount = (8.75 / 100) * item.faceValue;
              const hasTdsWaiver = tdsWaivers[item.folio];
              const tdsAmount = hasTdsWaiver ? 0 : (10 / 100) * interestAmount;
              const netPayout = interestAmount - tdsAmount;
              
              return (
                <TableRow key={index}>
                  <TableCell className="font-medium">{item.folio}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell className="text-right">₹{interestAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                  <TableCell>
                    {hasTdsWaiver ? (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                        Waiver Applied
                      </Badge>
                    ) : (
                      <>₹{tdsAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</>
                    )}
                  </TableCell>
                  <TableCell className="text-right">₹{netPayout.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                      Paid
                    </Badge>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      
      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        <Button variant="outline">
          <FileTextIcon className="h-4 w-4 mr-2" />
          Generate TDS Certificate
        </Button>
        <Button>
          <DownloadIcon className="h-4 w-4 mr-2" />
          Download Payment Report
        </Button>
      </div>
    </div>
  );
};
